// Fabric.js canvas service. Keeps side-effects out of components.
import api from '../../../../../utils/apiConfig';
import { Canvas, Rect, Circle as FabricCircle, Line, IText, Image as FabricImage, filters as fabricFilters, Pattern, util } from 'fabric';
import * as fabricModule from 'fabric';
import QRCode from 'qrcode';

export function initCanvas(canvasEl, { width = 800, height = 600, backgroundColor = '#ffffff' } = {}) {
  if (!canvasEl) return null;
  const canvas = new Canvas(canvasEl, { width, height, backgroundColor });
  return canvas;
}

// Guides overlay (non-layer) utilities
// Draws to contextTop during after:render so nothing appears in Layers panel
export function enableGuides(canvas, {
  color = 'rgba(255,255,255,0.25)',
  lineWidth = 1,
  showCenter = true,
  showThirds = false,
  grid = null, // { size: number, color?: string }
  margins = null, // { top?: number, right?: number, bottom?: number, left?: number }
} = {}) {
  if (!canvas) return;
  canvas.__showGuides = true;
  canvas.__guidesOpts = { color, lineWidth, showCenter, showThirds, grid, margins };

  if (canvas.__guidesListener) {
    canvas.requestRenderAll();
    return;
  }
  canvas.__guidesListener = () => {
    if (!canvas.__showGuides) return;
    const ctx = canvas.contextTop;
    if (!ctx) {
      console.warn('contextTop not available, guides disabled');
      return;
    }
    const w = canvas.getWidth();
    const h = canvas.getHeight();

    // Clear overlay
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const { color, lineWidth, showCenter, showThirds, grid, margins } = canvas.__guidesOpts || {};
    ctx.lineWidth = lineWidth || 1;
    ctx.strokeStyle = color || 'rgba(255,255,255,0.25)';

    // Grid
    if (grid && grid.size > 0) {
      const gColor = grid.color || 'rgba(255,255,255,0.08)';
      ctx.strokeStyle = gColor;
      for (let x = 0; x <= w; x += grid.size) {
        ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke();
      }
      for (let y = 0; y <= h; y += grid.size) {
        ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke();
      }
      ctx.strokeStyle = color || 'rgba(255,255,255,0.25)';
    }

    // Margins rectangle
    if (margins) {
      const top = margins.top || 0;
      const right = margins.right || 0;
      const bottom = margins.bottom || 0;
      const left = margins.left || 0;
      ctx.beginPath();
      ctx.rect(left + 0.5, top + 0.5, Math.max(0, w - left - right) - 1, Math.max(0, h - top - bottom) - 1);
      ctx.stroke();
    }

    // Center lines
    if (showCenter) {
      const cx = w / 2;
      const cy = h / 2;
      ctx.beginPath(); ctx.moveTo(cx + 0.5, 0); ctx.lineTo(cx + 0.5, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, cy + 0.5); ctx.lineTo(w, cy + 0.5); ctx.stroke();
    }

    // Rule of thirds
    if (showThirds) {
      const x1 = w / 3; const x2 = (2 * w) / 3; const y1 = h / 3; const y2 = (2 * h) / 3;
      ctx.beginPath(); ctx.moveTo(x1 + 0.5, 0); ctx.lineTo(x1 + 0.5, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x2 + 0.5, 0); ctx.lineTo(x2 + 0.5, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, y1 + 0.5); ctx.lineTo(w, y1 + 0.5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, y2 + 0.5); ctx.lineTo(w, y2 + 0.5); ctx.stroke();
    }

    ctx.restore();
  };

  canvas.on('after:render', canvas.__guidesListener);
  canvas.requestRenderAll();
}

export function disableGuides(canvas) {
  if (!canvas) return;
  canvas.__showGuides = false;
  const ctx = canvas.contextTop;
  if (ctx) {
    try {
      const w = canvas.getWidth();
      const h = canvas.getHeight();
      ctx.save(); 
      ctx.setTransform(1, 0, 0, 1, 0, 0); 
      ctx.clearRect(0, 0, w, h); 
      ctx.restore();
    } catch (error) {
      console.warn('Failed to clear guides overlay:', error);
    }
  }
  canvas.requestRenderAll();
}

export function setGuidesOptions(canvas, opts = {}) {
  if (!canvas) return;
  canvas.__guidesOpts = { ...(canvas.__guidesOpts || {}), ...opts };
  if (canvas.__showGuides) canvas.requestRenderAll();
}

// Apply an image texture (Pattern) as fill to an object or each child in a group
export async function applyTextureFillToObject(object, imageUrl, { repeat = 'repeat', patternTransform, tintColor, tintOpacity = 100, textureId, scalePct, rotationDeg } = {}) {
  if (!object || !imageUrl) return null;
  const imgEl = await util.loadImage(imageUrl);

  // Optional tint: draw image to offscreen canvas and colorize; then place an opaque base under it
  let sourceEl = imgEl;
  if (tintColor && typeof tintColor === 'string') {
    try {
      const w = imgEl.naturalWidth || imgEl.width;
      const h = imgEl.naturalHeight || imgEl.height;
      const off = document.createElement('canvas');
      off.width = Math.max(1, w);
      off.height = Math.max(1, h);
      const ctx = off.getContext('2d');
      // Draw original texture
      ctx.drawImage(imgEl, 0, 0, off.width, off.height);
      // Apply tint over drawn pixels
      ctx.globalCompositeOperation = 'source-atop';
      const alpha = Math.max(0, Math.min(1, (tintOpacity ?? 100) / 100));
      ctx.fillStyle = applyCssAlphaToColor(tintColor, alpha);
      ctx.fillRect(0, 0, off.width, off.height);
      // Add an opaque base under any transparent areas so pattern isn't see-through
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = applyCssAlphaToColor(tintColor, 1);
      ctx.fillRect(0, 0, off.width, off.height);
      // Reset
      ctx.globalCompositeOperation = 'source-over';
      sourceEl = off;
    } catch (_) {
      // fallback to original image source on any error
      sourceEl = imgEl;
    }
  }

  const pattern = new Pattern({ source: sourceEl, repeat });
  if (patternTransform) pattern.patternTransform = patternTransform;

  // If it's a group, apply to children; else to object itself
  if (object._objects && Array.isArray(object._objects)) {
    object._objects.forEach((child) => {
      if (typeof child.set === 'function') child.set({ fill: pattern });
    });
  } else if (typeof object.set === 'function') {
    object.set({ fill: pattern });
  }
  object.canvas?.requestRenderAll();
  // Persist texture meta for UI syncing
  try {
    const meta = object._texture || {};
    object._texture = {
      ...meta,
      enabled: true,
      imageUrl,
      repeat,
      patternTransform,
      tintColor,
      tintOpacity,
      textureId,
      scalePct,
      rotationDeg,
    };
  } catch (_) {}
  return object;
}

export function removeTextureFillFromObject(object, { fallbackFill = '#ffffff' } = {}) {
  if (!object) return null;
  const clear = (obj) => {
    if (typeof obj.set === 'function') {
      const currentFill = obj.fill;
      if (currentFill && typeof currentFill === 'object' && 'source' in currentFill) {
        obj.set({ fill: fallbackFill });
        try { if (obj._texture) obj._texture.enabled = false; } catch (_) {}
      }
    }
  };
  if (object._objects && Array.isArray(object._objects)) {
    object._objects.forEach(clear);
  } else {
    clear(object);
  }
  object.canvas?.requestRenderAll();
  return object;
}

export async function applyTextureToSelection(canvas, imageUrl, options = {}) {
  const obj = canvas?.getActiveObject?.();
  if (!obj) return null;
  const res = await applyTextureFillToObject(obj, imageUrl, options);
  canvas?.renderAll?.();
  return res;
}

// Helpers
function applyCssAlphaToColor(color, alpha) {
  try {
    // If color is already rgba or hsla, try to replace alpha
    const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbaMatch) {
      const parts = rgbaMatch[1].split(',').map(s => s.trim());
      const [r, g, b] = parts;
      return `rgba(${parseInt(r,10)}, ${parseInt(g,10)}, ${parseInt(b,10)}, ${alpha})`;
    }
    // Hex colors: #rgb, #rrggbb
    if (color.startsWith('#')) {
      let r, g, b;
      const hex = color.replace('#','');
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length >= 6) {
        r = parseInt(hex.slice(0,2), 16);
        g = parseInt(hex.slice(2,4), 16);
        b = parseInt(hex.slice(4,6), 16);
      } else {
        return color;
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // Fallback: return as-is (browser will apply without alpha)
    return color;
  } catch (_) {
    return color;
  }
}

export function removeTextureFromSelection(canvas, options = {}) {
  const obj = canvas?.getActiveObject?.();
  if (!obj) return null;
  const res = removeTextureFillFromObject(obj, options);
  canvas?.renderAll?.();
  return res;
}

// Save current canvas JSON as a reusable template asset
export async function saveTemplateJsonToLocation(canvas, { locationId, name }) {
  if (!canvas) throw new Error('Canvas not ready');
  if (!locationId) throw new Error('locationId is required');
  if (!name) throw new Error('name is required');

  // Embed image data similar to project save
  const elementToDataUrl = (el) => {
    try {
      const w = el.naturalWidth || el.width;
      const h = el.naturalHeight || el.height;
      const off = document.createElement('canvas');
      off.width = Math.max(1, w);
      off.height = Math.max(1, h);
      const ctx = off.getContext('2d');
      ctx.drawImage(el, 0, 0, off.width, off.height);
      return off.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  const allObjects = canvas.getObjects();
  // Ensure custom props like `name`, `selectable`, `evented`, and `__layerId` are serialized for ALL objects
  const generalOriginals = new Map();
  allObjects.forEach((obj) => {
    const orig = obj.toObject;
    generalOriginals.set(obj, orig);
    obj.toObject = function (props) {
      const base = orig.call(this, props);
      base.name = this.name;
      base.selectable = this.selectable;
      base.evented = this.evented;
      base.__layerId = this.__layerId;
      return base;
    };
  });

  const images = allObjects.filter((o) => o.type === 'image');
  const originals = new Map();
  images.forEach((img) => {
    const el = typeof img.getElement === 'function' ? img.getElement() : img._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (!dataUrl) return; // skip if cannot embed
    const orig = img.toObject;
    originals.set(img, orig);
    img.toObject = function (props) {
      const base = orig.call(this, props);
      base.src = dataUrl;
      base.selectable = this.selectable;
      base.evented = this.evented;
      base.name = this.name;
      base.__layerId = this.__layerId;
      return base;
    };
  });

  let restoreBgToObject = null;
  if (canvas.backgroundImage && canvas.backgroundImage.type === 'image') {
    const bg = canvas.backgroundImage;
    const el = typeof bg.getElement === 'function' ? bg.getElement() : bg._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (dataUrl) {
      const orig = bg.toObject;
      restoreBgToObject = () => { bg.toObject = orig; };
      bg.toObject = function (props) {
        const base = orig.call(this, props);
        base.src = dataUrl;
        base.name = this.name;
        base.__layerId = this.__layerId;
        return base;
      };
    }
  }

  const jsonObj = canvas.toJSON();

  images.forEach((img) => {
    const orig = originals.get(img);
    if (orig) img.toObject = orig;
  });
  generalOriginals.forEach((orig, obj) => { obj.toObject = orig; });
  if (restoreBgToObject) restoreBgToObject();

  const jsonStr = JSON.stringify(jsonObj);
  const base64 = typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(jsonStr))) : Buffer.from(jsonStr, 'utf-8').toString('base64');
  const dataUrl = `data:application/vnd.dymen.photoads.template+json;base64,${base64}`;
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  const res = await api.post('/photo-ads/assets', {
    locationId,
    name,
    dataUrl,
    mimeType: 'application/vnd.dymen.photoads.template+json',
    width,
    height,
  });
  return res.data;
}

// Update existing template asset by id
export async function updateTemplateJsonAsset(canvas, { assetId, name }) {
  if (!canvas) throw new Error('Canvas not ready');
  if (!assetId) throw new Error('assetId is required');

  // Ensure custom props like `name`, `selectable`, `evented`, and `__layerId` are serialized for ALL objects
  const allObjects = canvas.getObjects();
  const generalOriginals = new Map();
  allObjects.forEach((obj) => {
    const orig = obj.toObject;
    generalOriginals.set(obj, orig);
    obj.toObject = function (props) {
      const base = orig.call(this, props);
      base.name = this.name;
      base.selectable = this.selectable;
      base.evented = this.evented;
      base.__layerId = this.__layerId;
      return base;
    };
  });

  // Helper: convert an HTMLImageElement to data URL
  const elementToDataUrl = (el) => {
    try {
      const w = el.naturalWidth || el.width;
      const h = el.naturalHeight || el.height;
      const off = document.createElement('canvas');
      off.width = Math.max(1, w);
      off.height = Math.max(1, h);
      const ctx = off.getContext('2d');
      ctx.drawImage(el, 0, 0, off.width, off.height);
      return off.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  // Temporarily override toObject for images to embed data URLs
  const images = allObjects.filter((o) => o.type === 'image');
  const originals = new Map();
  images.forEach((img) => {
    const el = typeof img.getElement === 'function' ? img.getElement() : img._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (!dataUrl) return; // skip if cannot embed
    const orig = img.toObject;
    originals.set(img, orig);
    img.toObject = function (props) {
      const base = orig.call(this, props);
      base.src = dataUrl; // replace src with embedded data URL
      base.selectable = this.selectable;
      base.evented = this.evented;
      base.name = this.name;
      base.__layerId = this.__layerId;
      return base;
    };
  });

  // Handle background image embedding as well
  let restoreBgToObject = null;
  if (canvas.backgroundImage && canvas.backgroundImage.type === 'image') {
    const bg = canvas.backgroundImage;
    const el = typeof bg.getElement === 'function' ? bg.getElement() : bg._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (dataUrl) {
      const orig = bg.toObject;
      restoreBgToObject = () => { bg.toObject = orig; };
      bg.toObject = function (props) {
        const base = orig.call(this, props);
        base.src = dataUrl;
        base.name = this.name;
        base.__layerId = this.__layerId;
        return base;
      };
    }
  }

  const jsonObj = canvas.toJSON();

  // Restore patched methods
  images.forEach((img) => {
    const orig = originals.get(img);
    if (orig) img.toObject = orig;
  });
  generalOriginals.forEach((orig, obj) => { obj.toObject = orig; });
  if (restoreBgToObject) restoreBgToObject();

  const jsonStr = JSON.stringify(jsonObj);
  const base64 = typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(jsonStr))) : Buffer.from(jsonStr, 'utf-8').toString('base64');
  const dataUrl = `data:application/vnd.dymen.photoads.template+json;base64,${base64}`;
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  const res = await api.put(`/photo-ads/assets/${assetId}`, {
    name,
    dataUrl,
    mimeType: 'application/vnd.dymen.photoads.template+json',
    width,
    height,
  });
  return res.data?.data;
}

// Load template asset JSON by id
export async function loadTemplateAssetJson(assetId) {
  if (!assetId) throw new Error('assetId is required');
  const res = await api.get(`/photo-ads/assets/${assetId}/data`, { responseType: 'blob' });
  const text = await res.data.text();
  let json;
  try { json = JSON.parse(text); } catch (e) { throw new Error('Invalid template JSON'); }
  return json;
}

// Apply template JSON to canvas (clears existing)
export async function applyTemplateJson(canvas, json) {
  if (!canvas) throw new Error('Canvas not ready');
  canvas.clear();
  await new Promise((resolve) => canvas.loadFromJSON(json, () => {
    canvas.renderAll();
    // Wait one animation frame to ensure objects are fully mounted
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(() => resolve(), 0);
    }
  }));
}

// Populate placeholders on canvas with inventory data/images
export async function populateTemplateWithInventory(canvas, item, selectedImages) {
  if (!canvas || !item) return;
  // Getter supports dot-paths (e.g., prices.current)
  const getByPath = (root, path) => {
    try {
      return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), root);
    } catch (_) { return undefined; }
  };
  const getVal = (paths, fallback = '') => {
    for (const p of paths) {
      const v = p.includes('.') ? getByPath(item, p) : item[p];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
  };
  // Try common fields, prioritizing retail based on Prisma schema
  const priceRaw = getVal([
    'salePrice',
    'price',
    'retail',
    'retailPrice',
    'wholesale',
    'msrp',
    'prices.current',
    'pricing.current',
    'price.amount',
    'price.value',
    'cost'
  ], '');
  const formatPrice = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    // If val is an object like { amount: 12.3, currency: 'USD' }
    const objAmt = (typeof val === 'object') ? (val.amount ?? val.value) : undefined;
    const raw = objAmt !== undefined ? objAmt : val;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
    if (!isNaN(num)) return `$${num.toFixed(2)}`;
    // If not numeric, best effort prepend $
    const s = String(raw).trim();
    return s.startsWith('$') ? s : `$${s}`;
  };
  const priceVal = formatPrice(priceRaw);
  const titleVal = getVal(['name', 'title', 'sku'], '');
  const descVal = getVal(['description'], '');
  const skuVal = getVal(['sku'], '');
  const productUrl = getVal(['productUrl', 'url', 'link', 'website', 'webUrl'], '');

  // Determine candidate images: prefer explicitly selected images if provided
  const providedUrls = Array.isArray(selectedImages) ? selectedImages.filter(Boolean) : null;
  const images = Array.isArray(item.images) ? item.images
    : Array.isArray(item.photos) ? item.photos
    : [];
  const fallbackUrls = images.map((im) => typeof im === 'string' ? im : (im.secure_url || im.url)).filter(Boolean);
  const baseUrls = (providedUrls && providedUrls.length > 0) ? providedUrls : fallbackUrls;
  // Map through server proxy to avoid third-party CORS when drawing to canvas
  const apiBase = (api && api.defaults && api.defaults.baseURL) ? api.defaults.baseURL.replace(/\/$/, '') : '';
  const proxyPath = '/photo-ads/image-proxy?url=';
  const imageUrls = baseUrls.map((u) => {
    if (!u || typeof u !== 'string') return u;
    // Keep data URLs as-is
    if (u.startsWith('data:')) return u;
    // If already our API base prefix, keep as-is
    if (apiBase && u.startsWith(apiBase)) return u;
    // Otherwise route via proxy (apiBase expected to include /api)
    return `${apiBase}${proxyPath}${encodeURIComponent(u)}`;
  });

  // Helper to match placeholders like {{PRICE}}, {PRICE}, PRICE, and image tokens
  const norm = (s) => (typeof s === 'string' ? s.trim().toUpperCase() : '');
  const stripBraces = (s) => norm(s).replace(/^\{+\s*/,'').replace(/\s*\}+$/,'');
  const isPlaceholder = (val, key) => {
    const n = norm(val);
    return n === `{{${key}}}` || n === `{${key}}` || n === key;
  };
  const matchImageIndex = (val) => {
    const n = norm(val);
    let m = n.match(/^\{\{IMAGE(\d+)\}\}$/); if (m) return parseInt(m[1],10)-1;
    m = n.match(/^\{IMAGE(\d+)\}$/); if (m) return parseInt(m[1],10)-1;
    m = n.match(/^IMAGE(\d+)$/); if (m) return parseInt(m[1],10)-1;
    return null;
  };

  // Wait for canvas objects to be fully loaded
  let objs = canvas.getObjects();
  let attempts = 0;
  const maxAttempts = 5;
  
  // If no objects initially, wait and retry
  while (objs.length === 0 && attempts < maxAttempts) {
    console.debug(`populateTemplateWithInventory: No objects found, waiting... attempt ${attempts + 1}`);
    await new Promise(resolve => setTimeout(resolve, 100));
    objs = canvas.getObjects();
    attempts++;
  }
  
  let replacedText = 0;
  let replacedImages = 0;
  console.debug('populateTemplateWithInventory: initial objects', objs.length, objs.map(o => ({ t: o.type, name: o.name, text: o.text })));
  
  // Replace text placeholders (check both name and text tokens)
  objs.forEach((obj) => {
    const nm = obj.name || '';
    const tx = obj.text || '';
    if (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox') {
      if ((isPlaceholder(nm, 'PRICE') || isPlaceholder(tx, 'PRICE')) && (priceRaw !== '' && priceRaw !== null && priceRaw !== undefined)) { obj.set({ text: String(priceVal) }); replacedText++; }
      if ((isPlaceholder(nm, 'TITLE') || isPlaceholder(tx, 'TITLE')) && (titleVal !== '')) { obj.set({ text: String(titleVal) }); replacedText++; }
      if ((isPlaceholder(nm, 'DESCRIPTION') || isPlaceholder(tx, 'DESCRIPTION')) && (descVal !== '')) { obj.set({ text: String(descVal) }); replacedText++; }
      if ((isPlaceholder(nm, 'SKU') || isPlaceholder(tx, 'SKU')) && (skuVal !== '')) { obj.set({ text: String(skuVal) }); replacedText++; }
    }
  });

  // Handle QR Code placeholders
  // Create a copy of objects array since we'll be modifying the canvas during iteration
  const qrObjects = objs.slice();
  console.debug('QR Code Debug - Processing objects:', qrObjects.length, 'productUrl:', productUrl);
  for (let i = 0; i < qrObjects.length; i++) {
    const obj = qrObjects[i];
    const nm = obj.name || '';
    
    console.debug('QR Code Debug - Object:', i, 'name:', nm, 'type:', obj.type, 'qrCodePlaceholder:', obj.qrCodePlaceholder);
    
    // Skip if object was already removed from canvas
    if (!canvas.getObjects().includes(obj)) {
      console.debug('QR Code Debug - Object already removed, skipping');
      continue;
    }
    
    // Check if this is a QR code placeholder - support all object types
    const isQRPlaceholder = isPlaceholder(nm, 'QR_CODE') || obj.qrCodePlaceholder;
    console.debug('QR Code Debug - Placeholder check:', isQRPlaceholder, 'for object type:', obj.type);
    
    if (isQRPlaceholder) {
      console.debug('QR Code Debug - Found QR placeholder!', nm, obj.qrCodePlaceholder);
      if (productUrl && productUrl.trim() !== '') {
        console.debug('QR Code Debug - Generating QR for URL:', productUrl);
        try {
          // Generate QR code as data URL
          const qrDataUrl = await QRCode.toDataURL(productUrl, {
            width: 300,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
          
          // Get placeholder position and size
          const left = obj.left;
          const top = obj.top;
          const width = obj.width;
          const height = obj.height;
          const scaleX = obj.scaleX || 1;
          const scaleY = obj.scaleY || 1;
          
          // Create QR code image
          const qrImage = await addImageLayerFromUrl(canvas, qrDataUrl, { center: false, scaleToFit: false });
          
          // Position and scale the QR code to match placeholder
          qrImage.set({
            left: left,
            top: top,
            originX: obj.originX || 'left',
            originY: obj.originY || 'top'
          });
          
          // Scale to fit placeholder dimensions
          if (width && height && qrImage.width && qrImage.height) {
            const targetWidth = width * scaleX;
            const targetHeight = height * scaleY;
            const scale = Math.min(targetWidth / qrImage.width, targetHeight / qrImage.height);
            if (scale && isFinite(scale) && scale > 0) {
              qrImage.scale(scale);
            }
          }
          
          // Remove placeholder and add QR code
          canvas.remove(obj);
          replacedImages++;
          
        } catch (error) {
          console.error('Failed to generate QR code:', error);
        }
      } else {
        console.debug('QR Code Debug - No productUrl available:', productUrl);
      }
    } else {
      console.debug('QR Code Debug - Not a QR placeholder');
    }
  }

  // Replace image placeholders
  // Create a copy of objects array since we'll be modifying the canvas during iteration
  const imageObjects = objs.slice();
  for (let i = 0; i < imageObjects.length; i++) {
    const obj = imageObjects[i];
    const nm = obj.name || '';
    const tx = obj.text || '';
    const up = norm(nm);
    
    // Skip if object was already removed from canvas
    if (!canvas.getObjects().includes(obj)) continue;
    
    let idx = matchImageIndex(nm);
    if (idx == null) idx = matchImageIndex(tx);
    if (idx != null) {
      const url = imageUrls[idx];
      if (!url) continue;
      const left = obj.left, top = obj.top, angle = obj.angle, scaleX = obj.scaleX, scaleY = obj.scaleY, width = obj.width, height = obj.height, originX = obj.originX, originY = obj.originY;
      try {
        const image = await addImageLayerFromUrl(canvas, url, { center: false, scaleToFit: false });
        image.set({ left, top, angle, originX, originY });
        // scale to fit previous box footprint if available
        if (width && height && image.width && image.height) {
          const sx = (width * (scaleX || 1)) / image.width;
          const sy = (height * (scaleY || 1)) / image.height;
          const s = Math.min(sx, sy);
          if (s && isFinite(s) && s > 0) image.scale(s);
        }
        canvas.remove(obj);
        replacedImages++;
      } catch (_) {}
    }
    // Support a generic IMAGE placeholder mapping to first image
    if ((up === '{{IMAGE}}' || up === '{IMAGE}' || up === 'IMAGE') && imageUrls[0]) {
      // Skip if object was already removed from canvas
      if (!canvas.getObjects().includes(obj)) continue;
      
      const left = obj.left, top = obj.top, angle = obj.angle, scaleX = obj.scaleX, scaleY = obj.scaleY, width = obj.width, height = obj.height, originX = obj.originX, originY = obj.originY;
      try {
        const image = await addImageLayerFromUrl(canvas, imageUrls[0], { center: false, scaleToFit: false });
        image.set({ left, top, angle, originX, originY });
        if (width && height && image.width && image.height) {
          const sx = (width * (scaleX || 1)) / image.width;
          const sy = (height * (scaleY || 1)) / image.height;
          const s = Math.min(sx, sy);
          if (s && isFinite(s) && s > 0) image.scale(s);
        }
        canvas.remove(obj);
        replacedImages++;
      } catch (_) {}
    }
  }

  console.debug('populateTemplateWithInventory:', {
    titleVal, priceVal, priceRaw, skuVal, descVal, imageCount: imageUrls.length,
    replacedText, replacedImages,
    placeholdersOnCanvas: objs.map(o => ({ name: o.name, text: o.text })).filter(v => (v.name||v.text))
  });
  canvas.renderAll();

  // If nothing was replaced and objects may not have been ready, do a single retry on next frame
  if (replacedText === 0 && replacedImages === 0) {
    await new Promise(requestAnimationFrame);
    const lateObjs = canvas.getObjects();
    let lateText = 0, lateImages = 0;
    lateObjs.forEach((obj) => {
      const nm = obj.name || '';
      const tx = obj.text || '';
      if (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox') {
        if ((isPlaceholder(nm, 'PRICE') || isPlaceholder(tx, 'PRICE')) && (priceVal !== '')) { obj.set({ text: String(priceVal) }); lateText++; }
        if ((isPlaceholder(nm, 'TITLE') || isPlaceholder(tx, 'TITLE')) && (titleVal !== '')) { obj.set({ text: String(titleVal) }); lateText++; }
        if ((isPlaceholder(nm, 'DESCRIPTION') || isPlaceholder(tx, 'DESCRIPTION')) && (descVal !== '')) { obj.set({ text: String(descVal) }); lateText++; }
        if ((isPlaceholder(nm, 'SKU') || isPlaceholder(tx, 'SKU')) && (skuVal !== '')) { obj.set({ text: String(skuVal) }); lateText++; }
      }
    });
    for (let i = 0; i < lateObjs.length; i++) {
      const obj = lateObjs[i];
      const nm = obj.name || '';
      const tx = obj.text || '';
      const up = norm(nm);
      let idx = matchImageIndex(nm);
      if (idx == null) idx = matchImageIndex(tx);
      if (idx != null) {
        const url = imageUrls[idx];
        if (url) {
          const left = obj.left, top = obj.top, angle = obj.angle, scaleX = obj.scaleX, scaleY = obj.scaleY, width = obj.width, height = obj.height, originX = obj.originX, originY = obj.originY;
          try {
            const image = await addImageLayerFromUrl(canvas, url, { center: false, scaleToFit: false });
            image.set({ left, top, angle, originX, originY });
            if (width && height && image.width && image.height) {
              const sx = (width * (scaleX || 1)) / image.width;
              const sy = (height * (scaleY || 1)) / image.height;
              const s = Math.min(sx, sy);
              if (s && isFinite(s) && s > 0) image.scale(s);
            }
            canvas.remove(obj);
            lateImages++;
          } catch (_) {}
        }
      }
      if ((up === '{{IMAGE}}' || up === '{IMAGE}' || up === 'IMAGE') && imageUrls[0]) {
        const left = obj.left, top = obj.top, angle = obj.angle, scaleX = obj.scaleX, scaleY = obj.scaleY, width = obj.width, height = obj.height, originX = obj.originX, originY = obj.originY;
        try {
          const image = await addImageLayerFromUrl(canvas, imageUrls[0], { center: false, scaleToFit: false });
          image.set({ left, top, angle, originX, originY });
          if (width && height && image.width && image.height) {
            const sx = (width * (scaleX || 1)) / image.width;
            const sy = (height * (scaleY || 1)) / image.height;
            const s = Math.min(sx, sy);
            if (s && isFinite(s) && s > 0) image.scale(s);
          }
          canvas.remove(obj);
          lateImages++;
        } catch (_) {}
      }
    }
    if (lateText || lateImages) {
      console.debug('populateTemplateWithInventory: retry pass replaced', { lateText, lateImages, lateCount: lateObjs.length });
      canvas.renderAll();
    }
  }
}

// Update existing project asset by id (preserve layers via embedded data URLs)
export async function updateProjectJsonAsset(canvas, { assetId, name }) {
  if (!canvas) throw new Error('Canvas not ready');
  if (!assetId) throw new Error('assetId is required');

  // Ensure custom props like `name`, `selectable`, `evented` are serialized for ALL objects
  const allObjects = canvas.getObjects();
  const generalOriginals = new Map();
  allObjects.forEach((obj) => {
    const orig = obj.toObject;
    generalOriginals.set(obj, orig);
    obj.toObject = function (props) {
      const base = orig.call(this, props);
      base.name = this.name;
      base.selectable = this.selectable;
      base.evented = this.evented;
      return base;
    };
  });

  // Helper: convert an HTMLImageElement to data URL
  const elementToDataUrl = (el) => {
    try {
      const w = el.naturalWidth || el.width;
      const h = el.naturalHeight || el.height;
      const off = document.createElement('canvas');
      off.width = Math.max(1, w);
      off.height = Math.max(1, h);
      const ctx = off.getContext('2d');
      ctx.drawImage(el, 0, 0, off.width, off.height);
      return off.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  // Temporarily override toObject for images to embed data URLs
  const images = canvas.getObjects().filter((o) => o.type === 'image');
  const originals = new Map();
  images.forEach((img) => {
    const el = typeof img.getElement === 'function' ? img.getElement() : img._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (!dataUrl) return;
    const orig = img.toObject;
    originals.set(img, orig);
    img.toObject = function (props) {
      const base = orig.call(this, props);
      base.src = dataUrl;
      base.selectable = this.selectable;
      base.evented = this.evented;
      return base;
    };
  });

  let restoreBgToObject = null;
  if (canvas.backgroundImage && canvas.backgroundImage.type === 'image') {
    const bg = canvas.backgroundImage;
    const el = typeof bg.getElement === 'function' ? bg.getElement() : bg._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (dataUrl) {
      const orig = bg.toObject;
      restoreBgToObject = () => { bg.toObject = orig; };
      bg.toObject = function (props) {
        const base = orig.call(this, props);
        base.src = dataUrl;
        return base;
      };
    }
  }

  const jsonObj = canvas.toJSON();

  // Restore originals
  images.forEach((img) => {
    const orig = originals.get(img);
    if (orig) img.toObject = orig;
  });
  // Restore general overrides
  generalOriginals.forEach((orig, obj) => { obj.toObject = orig; });
  if (restoreBgToObject) restoreBgToObject();

  const jsonStr = JSON.stringify(jsonObj);
  const base64 = typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(jsonStr))) : Buffer.from(jsonStr, 'utf-8').toString('base64');
  const dataUrl = `data:application/vnd.dymen.photoads+json;base64,${base64}`;
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  const res = await api.put(`/photo-ads/assets/${assetId}`, {
    name,
    dataUrl,
    mimeType: 'application/vnd.dymen.photoads+json',
    width,
    height,
  });
  return res.data?.data;
}

// Add an image as a new layer (not background)
export function addImageLayerFromUrl(canvas, url, { center = true, scaleToFit = true, timeoutMs = 15000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!canvas || !url) return reject(new Error('Canvas and url are required'));
    const fabricNS = (typeof window !== 'undefined' && window.fabric) ? window.fabric : null;
    const ImageClass = fabricNS?.Image || FabricImage;
    if (!ImageClass) return reject(new Error('fabric Image class not available'));

    const imgEl = new Image();
    imgEl.crossOrigin = 'anonymous';
    let settled = false;
    const done = (fn) => (arg) => { if (settled) return; settled = true; fn(arg); };
    const timer = setTimeout(done(() => reject(new Error('Image load timed out'))), timeoutMs);
    const clear = () => { clearTimeout(timer); imgEl.onload = null; imgEl.onerror = null; };
    imgEl.onerror = (...a) => { clear(); done((e) => reject(new Error('Failed to load image')))(...a); };
    imgEl.onload = () => {
      clear();
      try {
        const image = new ImageClass(imgEl);
        if (!image.__layerId) image.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        if (scaleToFit) {
          const cw = canvas.getWidth();
          const ch = canvas.getHeight();
          const iw = image.width || imgEl.naturalWidth;
          const ih = image.height || imgEl.naturalHeight;
          const scale = Math.min(cw / iw, ch / ih, 1);
          if (scale && isFinite(scale) && scale > 0) image.scale(scale);
        }
        if (center) {
          image.set({ left: canvas.getWidth() / 2, top: canvas.getHeight() / 2, originX: 'center', originY: 'center' });
        }
        image.set({ selectable: true, evented: true });
        canvas.add(image);
        canvas.setActiveObject(image);
        canvas.renderAll();
        resolve(image);
      } catch (err) {
        reject(err);
      }
    };
    imgEl.src = url;
  });
}

// Save full project (Fabric JSON) to server so layers are preserved
export async function saveProjectJsonToLocation(canvas, { locationId, name }) {
  if (!canvas) throw new Error('Canvas not ready');
  if (!locationId) throw new Error('locationId is required');
  if (!name) throw new Error('name is required');
  // Helper: convert an HTMLImageElement to data URL
  const elementToDataUrl = (el) => {
    try {
      const w = el.naturalWidth || el.width;
      const h = el.naturalHeight || el.height;
      const off = document.createElement('canvas');
      off.width = Math.max(1, w);
      off.height = Math.max(1, h);
      const ctx = off.getContext('2d');
      ctx.drawImage(el, 0, 0, off.width, off.height);
      return off.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  // Ensure custom props like `name`, `selectable`, `evented`, and `__layerId` are serialized for ALL objects
  const allObjects = canvas.getObjects();
  const generalOriginals = new Map();
  allObjects.forEach((obj) => {
    const orig = obj.toObject;
    generalOriginals.set(obj, orig);
    obj.toObject = function (props) {
      const base = orig.call(this, props);
      base.name = this.name;
      base.selectable = this.selectable;
      base.evented = this.evented;
      base.__layerId = this.__layerId;
      return base;
    };
  });

  // Temporarily override toObject for images to embed data URLs
  const images = allObjects.filter((o) => o.type === 'image');
  const originals = new Map();
  images.forEach((img) => {
    const el = typeof img.getElement === 'function' ? img.getElement() : img._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (!dataUrl) return; // skip if cannot embed
    const orig = img.toObject;
    originals.set(img, orig);
    img.toObject = function (props) {
      const base = orig.call(this, props);
      base.src = dataUrl; // replace src with embedded data URL
      // preserve common flags and custom id
      base.selectable = this.selectable;
      base.evented = this.evented;
      base.name = this.name;
      base.__layerId = this.__layerId;
      return base;
    };
  });

  // Handle background image embedding as well
  let restoreBgToObject = null;
  if (canvas.backgroundImage && canvas.backgroundImage.type === 'image') {
    const bg = canvas.backgroundImage;
    const el = typeof bg.getElement === 'function' ? bg.getElement() : bg._originalElement;
    const dataUrl = el ? elementToDataUrl(el) : null;
    if (dataUrl) {
      const orig = bg.toObject;
      restoreBgToObject = () => { bg.toObject = orig; };
      bg.toObject = function (props) {
        const base = orig.call(this, props);
        base.src = dataUrl;
        return base;
      };
    }
  }

  const jsonObj = canvas.toJSON();

  // Restore patched methods
  images.forEach((img) => {
    const orig = originals.get(img);
    if (orig) img.toObject = orig;
  });
  // Restore general overrides
  generalOriginals.forEach((orig, obj) => { obj.toObject = orig; });
  if (restoreBgToObject) restoreBgToObject();

  const jsonStr = JSON.stringify(jsonObj);
  const base64 = typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(jsonStr))) : Buffer.from(jsonStr, 'utf-8').toString('base64');
  const dataUrl = `data:application/vnd.dymen.photoads+json;base64,${base64}`;
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  const res = await api.post('/photo-ads/assets', {
    locationId,
    name, // same display name for the project record
    dataUrl,
    mimeType: 'application/vnd.dymen.photoads+json',
    width,
    height,
  });
  return res.data;
}

export function disposeCanvas(canvas) {
  try { canvas?.dispose?.(); } catch (_) {}
}

export function addText(canvas, { text = 'Click to edit', left, top, fontFamily, fontSize, fill }) {
  const t = new IText(text, { left, top, fontFamily, fontSize, fill });
  if (!t.__layerId) t.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  canvas.add(t); canvas.setActiveObject(t); canvas.renderAll();
  return t;
}

export function addRect(canvas, { left, top, width = 100, height = 100, fill, stroke, strokeWidth }) {
  const r = new Rect({ left, top, width, height, fill, stroke, strokeWidth });
  if (!r.__layerId) r.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  canvas.add(r); canvas.setActiveObject(r); canvas.renderAll();
  return r;
}

export function addCircle(canvas, { left, top, radius = 50, fill, stroke, strokeWidth }) {
  const c = new FabricCircle({ left, top, radius, fill, stroke, strokeWidth });
  if (!c.__layerId) c.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  canvas.add(c); canvas.setActiveObject(c); canvas.renderAll();
  return c;
}

export function addLine(canvas, { points = [50, 50, 200, 200], stroke, strokeWidth }) {
  const l = new Line(points, { stroke, strokeWidth });
  if (!l.__layerId) l.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  canvas.add(l); canvas.setActiveObject(l); canvas.renderAll();
  return l;
}

// Add SVG shape/group from an SVG string
export function addSvgShape(canvas, svgString, { center = true, fill, stroke, strokeWidth = 1, targetWidth = 150 } = {}) {
  return new Promise((resolve, reject) => {
    if (!canvas || !svgString) return reject(new Error('Canvas and svgString are required'));
    const fabricApi = (typeof window !== 'undefined' && window.fabric) ? window.fabric : fabricModule;
    if (!fabricApi) return reject(new Error('fabric is not available'));

    const applyStyle = (obj) => {
      if (!obj || typeof obj.set !== 'function') return;
      // Only set fill/stroke if provided so default SVG styling works otherwise
      if (fill !== undefined) obj.set({ fill });
      if (stroke !== undefined) obj.set({ stroke });
      if (strokeWidth !== undefined) obj.set({ strokeWidth });
    };

    const finalize = (objects, options) => {
      try {
        let obj;
        // Group objects if multiple returned
        if (Array.isArray(objects) && objects.length > 1) {
          obj = fabricApi.util.groupSVGElements(objects, options || {});
        } else {
          obj = Array.isArray(objects) ? objects[0] : objects;
        }
        if (!obj) return reject(new Error('Failed to create SVG object'));
        // Assign stable id
        if (!obj.__layerId) obj.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

        // Apply styling recursively if group
        if (obj._objects && Array.isArray(obj._objects)) {
          obj._objects.forEach(applyStyle);
        } else {
          applyStyle(obj);
        }

        // Scale to target width while preserving aspect ratio
        const cw = canvas.getWidth();
        const ch = canvas.getHeight();
        obj.scale(1);
        obj.setCoords();
        const bounds = obj.getBoundingRect(true);
        if (bounds.width > 0 && isFinite(bounds.width) && targetWidth > 0) {
          const scale = Math.min(targetWidth / bounds.width, 4);
          if (scale && scale > 0) obj.scale(scale);
        }

        // Position
        if (center) {
          obj.set({ left: cw / 2, top: ch / 2, originX: 'center', originY: 'center' });
        }
        obj.set({ selectable: true, evented: true });
        canvas.add(obj);
        canvas.setActiveObject(obj);
        canvas.renderAll();
        resolve(obj);
      } catch (e) {
        reject(e);
      }
    };

    // Prefer loadSVGFromString if available (Fabric v6: returns Promise)
    if (typeof fabricApi.loadSVGFromString === 'function') {
      try {
        Promise.resolve(fabricApi.loadSVGFromString(svgString))
          .then((res) => {
            // Some versions return { objects, options }, older may return [objects, options]
            const objects = res?.objects || res?.[0];
            const options = res?.options || res?.[1];
            finalize(objects, options);
          })
          .catch(reject);
      } catch (e) {
        reject(e);
      }
      return;
    }
    // Fallback: parse via DOM and use loadSVGFromURL with data URL
    try {
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const cleanup = () => URL.revokeObjectURL(url);
      if (typeof fabricApi.loadSVGFromURL === 'function') {
        Promise.resolve(fabricApi.loadSVGFromURL(url))
          .then((res) => {
            const objects = res?.objects || res?.[0];
            const options = res?.options || res?.[1];
            cleanup();
            finalize(objects, options);
          })
          .catch((err) => { cleanup(); reject(err); });
      } else {
        cleanup();
        reject(new Error('No SVG loader available in fabric'));
      }
    } catch (err) {
      reject(err);
    }
  });
}

export function applyImageFilters(canvas, object, { brightness = 100, contrast = 100, saturation = 100 }, extraFilters = []) {
  if (!object || object.type !== 'image') return;
  object.filters = [];
  extraFilters.forEach((f) => object.filters.push(f));
  object.filters.push(
    new fabricFilters.Brightness({ brightness: (brightness - 100) / 100 }),
    new fabricFilters.Contrast({ contrast: (contrast - 100) / 100 }),
    new fabricFilters.Saturation({ saturation: (saturation - 100) / 100 })
  );
  object.applyFilters();
  canvas.renderAll();
}

// Export/Save/Load helpers
export function downloadImage(canvas, filename = `photo-ads-${Date.now()}.png`) {
  if (!canvas) return;
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL();
  link.click();
}

export function saveProject(canvas, key = 'photoAdsProject') {
  if (!canvas) return false;
  // Include names and basic flags in local saves as well
  const allObjects = canvas.getObjects();
  const generalOriginals = new Map();
  allObjects.forEach((obj) => {
    const orig = obj.toObject;
    generalOriginals.set(obj, orig);
    obj.toObject = function (props) {
      const base = orig.call(this, props);
      base.name = this.name;
      base.selectable = this.selectable;
      base.evented = this.evented;
      return base;
    };
  });
  const json = JSON.stringify(canvas.toJSON());
  generalOriginals.forEach((orig, obj) => { obj.toObject = orig; });
  localStorage.setItem(key, json);
  return true;
}

export function loadProject(canvas, key = 'photoAdsProject', onLoaded) {
  if (!canvas) return false;
  const json = localStorage.getItem(key);
  if (!json) return false;
  canvas.loadFromJSON(json, () => {
    canvas.renderAll();
    onLoaded?.();
  });
  return true;
}

// Save canvas image to server database tied to a locationId
export async function saveImageToLocation(canvas, { locationId, name, mimeType = 'image/png' }) {
  if (!canvas) throw new Error('Canvas not ready');
  if (!locationId) throw new Error('locationId is required');
  if (!name) throw new Error('name is required');
  const format = mimeType.includes('jpeg') ? 'jpeg' : 'png';
  const dataUrl = canvas.toDataURL({ format });
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  const res = await api.post('/photo-ads/assets', {
    locationId,
    name,
    dataUrl,
    mimeType: format === 'jpeg' ? 'image/jpeg' : 'image/png',
    width,
    height,
  });
  return res.data;
}

// Load an image URL into the canvas as background
export function setCanvasBackgroundFromUrl(canvas, url, { resizeToImage = false, timeoutMs = 15000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!canvas || !url) return reject(new Error('Canvas and url are required'));
    const fabricNS = (typeof window !== 'undefined' && window.fabric) ? window.fabric : null;
    const ImageClass = fabricNS?.Image || FabricImage;
    if (!ImageClass) return reject(new Error('fabric Image class not available'));

    const imgEl = new Image();
    imgEl.crossOrigin = 'anonymous';
    let settled = false;
    const onDone = (fn) => (arg) => {
      if (settled) return; settled = true; fn(arg);
    };
    const onLoad = onDone(() => {
      try {
        const fabricImage = new ImageClass(imgEl);
        if (resizeToImage) {
          canvas.setWidth(fabricImage.width || canvas.getWidth());
          canvas.setHeight(fabricImage.height || canvas.getHeight());
        }
        // Prefer API if available
        if (typeof canvas.setBackgroundImage === 'function') {
          if (!fabricImage.__layerId) fabricImage.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          canvas.setBackgroundImage(fabricImage, canvas.renderAll.bind(canvas), {
            left: 0,
            top: 0,
            originX: 'left',
            originY: 'top'
          });
        } else if ('backgroundImage' in canvas) {
          // Fabric v6-style property assignment
          if (!fabricImage.__layerId) fabricImage.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          canvas.backgroundImage = fabricImage;
          if (typeof canvas.requestRenderAll === 'function') canvas.requestRenderAll();
          else canvas.renderAll();
        } else {
          // Fallback: add as bottom-most, locked layer
          if (!fabricImage.__layerId) fabricImage.__layerId = `L${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          fabricImage.set({ left: 0, top: 0, selectable: false, evented: false, hasControls: false, hoverCursor: 'default' });
          canvas.add(fabricImage);
          if (typeof fabricImage.sendToBack === 'function') fabricImage.sendToBack();
          canvas.renderAll();
        }
        resolve(fabricImage);
      } catch (err) {
        reject(err);
      }
    });
    const onError = onDone((e) => reject(new Error('Failed to load image: ' + (e?.message || 'unknown'))));
    const timer = setTimeout(onDone(() => reject(new Error('Image load timed out'))), timeoutMs);
    const clearAll = () => { clearTimeout(timer); imgEl.onload = null; imgEl.onerror = null; };

    imgEl.onload = (...args) => { clearAll(); onLoad(...args); };
    imgEl.onerror = (...args) => { clearAll(); onError(...args); };
    imgEl.src = url;
  });
}
